import {
  type Box3,
  cellHeight,
  gridHeightScale,
  MAX_PROJECTED_BOXES,
  painterKey,
  riseAt,
} from "../projection/iso-projection";
import type { HeatmapColumn } from "./heatmap-context";

export const HEATMAP_PROJECTION_PAD = 24;
export const HEATMAP_PROJECTION_FACES = 3;

export interface HeatmapCellRect {
  column: number;
  row: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

export const heatmapProjectionFits = (columns: number, rows: number): boolean =>
  columns > 0 &&
  rows > 0 &&
  columns * rows * HEATMAP_PROJECTION_FACES <= MAX_PROJECTED_BOXES;

export const heatmapMaxCount = (data: readonly HeatmapColumn[]): number => {
  let max = 0;
  for (const column of data) {
    for (const bin of column.bins) {
      const value = Number(bin.count);
      if (Number.isFinite(value) && value > max) max = value;
    }
  }
  return max;
};

export type HeatmapProjectionValue = (
  count: number,
  column: number,
  row: number
) => number;

export type HeatmapBoxHeight = (
  count: number | null | undefined,
  column: number,
  row: number
) => number;

const finiteOr0 = (value: number): number => (Number.isFinite(value) ? value : 0);

const heatmapMaxValue = (
  data: readonly HeatmapColumn[],
  valueOf: HeatmapProjectionValue
): number => {
  let max = 0;
  data.forEach((column, columnIndex) => {
    column.bins.forEach((bin, rowIndex) => {
      const value = finiteOr0(valueOf(Number(bin.count ?? 0), columnIndex, rowIndex));
      if (value > max) max = value;
    });
  });
  return max;
};

export const createHeatmapBoxHeight = (
  data: readonly HeatmapColumn[],
  unit: number,
  heightScale?: number,
  valueOf?: HeatmapProjectionValue,
  valueMax?: number
): HeatmapBoxHeight => {
  const columns = data.length;
  const rows = data[0]?.bins.length ?? 0;
  const max = valueOf
    ? (valueMax ?? heatmapMaxValue(data, valueOf))
    : heatmapMaxCount(data);
  const scale = heightScale ?? gridHeightScale(columns, rows);
  const size = Math.max(0, unit);
  return (count, column, row) => {
    const raw = Number(count ?? 0);
    const value = valueOf ? valueOf(raw, column, row) : raw;
    return cellHeight(finiteOr0(value), max, scale) * size;
  };
};

export const heatmapMaxBoxHeight = (
  data: readonly HeatmapColumn[],
  boxHeight: HeatmapBoxHeight
): number => {
  let max = 0;
  data.forEach((column, columnIndex) => {
    column.bins.forEach((bin, rowIndex) => {
      const height = boxHeight(bin.count, columnIndex, rowIndex);
      if (height > max) max = height;
    });
  });
  return max;
};

export const heatmapCellBoxAt =
  (cell: HeatmapCellRect, z: number, columns: number, rows: number) =>
  (e: number): Box3 => ({
    x0: cell.x,
    x1: cell.x + cell.width,
    y0: cell.y,
    y1: cell.y + cell.height,
    z0: 0,
    z1: z * riseAt(e, cell.column, columns, cell.row, rows),
  });

export const heatmapCellIndex = (column: number, row: number, rows: number): number =>
  column * Math.max(1, rows) + row;

export const sortHeatmapCellsForPainter = <T extends { column: number; row: number }>(
  cells: readonly T[]
): T[] =>
  cells
    .map((cell, index) => ({ cell, index, key: painterKey(cell.column, cell.row) }))
    .sort((a, b) => a.key - b.key || a.index - b.index)
    .map((item) => item.cell);

export const HEATMAP_AXIS_FADE: number[] = [0, 0.4];
